import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Download, ExternalLink, X } from 'lucide-react';
import { downloadPrelimsPdf, studyAPI, editorialsAPI } from '../../services/api';
import './Dashboard.css';

/* ── Flat illustrations for the card panels ── */
const ArtPractice = () => (
  <svg viewBox="0 0 200 180" aria-hidden="true">
    <rect x="48" y="14" width="104" height="138" rx="10" fill="#fffcf5" />
    <rect x="62" y="30" width="60" height="8" rx="4" fill="#2c3f46" />
    <rect x="62" y="46" width="76" height="5" rx="2.5" fill="#cdb98f" />
    <rect x="62" y="56" width="58" height="5" rx="2.5" fill="#cdb98f" />
    {[0, 1, 2, 3].map(i => (
      <g key={i}>
        <circle cx="70" cy={80 + i * 17} r="6" fill={i === 1 ? '#d3553b' : 'none'} stroke={i === 1 ? '#d3553b' : '#2c3f46'} strokeWidth="2" />
        <rect x="84" y={77.5 + i * 17} width={i === 2 ? 40 : 52} height="5" rx="2.5" fill="#d9cfb5" />
      </g>
    ))}
    <path d="M66.5 97 l2.8 3 l5-6" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <g transform="rotate(32 152 128)">
      <rect x="145" y="90" width="14" height="58" rx="3" fill="#f2b84b" />
      <rect x="145" y="90" width="14" height="11" rx="3" fill="#d3553b" />
      <path d="M145 148 L152 166 L159 148 Z" fill="#f8e5c7" />
      <path d="M149.5 159 L152 166 L154.5 159 Z" fill="#2c3f46" />
    </g>
  </svg>
);

const ArtEditorial = () => (
  <svg viewBox="0 0 200 180" aria-hidden="true">
    <rect x="62" y="26" width="100" height="130" rx="8" fill="#e6c283" transform="rotate(7 112 90)" />
    <rect x="38" y="18" width="112" height="140" rx="8" fill="#fffcf5" />
    <rect x="50" y="31" width="88" height="12" rx="3" fill="#2c3f46" />
    <rect x="50" y="51" width="88" height="48" rx="5" fill="#69b9af" />
    <path d="M50 99 L78 68 L95 86 L110 72 L138 99 Z" fill="#4b8376" />
    <circle cx="123" cy="63" r="6" fill="#f2b84b" />
    {[110, 121, 132, 143].map((y, i) => (
      <rect key={y} x="50" y={y} width={[88, 62, 88, 46][i]} height="5" rx="2.5" fill="#cdb98f" />
    ))}
  </svg>
);

const ArtBacklog = () => (
  <svg viewBox="0 0 200 180" aria-hidden="true">
    <rect x="42" y="108" width="116" height="54" rx="10" fill="#f8e5c7" transform="rotate(-5 100 135)" />
    <rect x="44" y="80" width="116" height="54" rx="10" fill="#fdf6e6" transform="rotate(4 100 107)" />
    <rect x="42" y="44" width="116" height="60" rx="10" fill="#fffcf5" />
    <rect x="56" y="60" width="54" height="7" rx="3.5" fill="#2c3f46" />
    <rect x="56" y="74" width="82" height="5" rx="2.5" fill="#cdb98f" />
    <rect x="56" y="84" width="62" height="5" rx="2.5" fill="#cdb98f" />
    <path d="M136 44 h18 v36 l-9 -7 l-9 7 Z" fill="#2c3f46" />
  </svg>
);

const ArtPdf = () => (
  <svg viewBox="0 0 200 180" aria-hidden="true">
    <path d="M54 16 H122 L152 46 V152 a8 8 0 0 1 -8 8 H54 a8 8 0 0 1 -8 -8 V24 a8 8 0 0 1 8 -8 Z" fill="#fffcf5" />
    <path d="M122 16 V46 H152 Z" fill="#e6dcc3" />
    <rect x="58" y="66" width="46" height="21" rx="5" fill="#d3553b" />
    <text x="81" y="81.5" textAnchor="middle" fontSize="12" fontWeight="700" fill="#fff" fontFamily="DM Sans, Arial, sans-serif">PDF</text>
    <rect x="58" y="98" width="80" height="5" rx="2.5" fill="#cdb98f" />
    <rect x="58" y="109" width="80" height="5" rx="2.5" fill="#cdb98f" />
    <rect x="58" y="120" width="52" height="5" rx="2.5" fill="#cdb98f" />
    <circle cx="138" cy="138" r="23" fill="#f2b84b" />
    <path d="M138 126 v20 M128.5 139 l9.5 9.5 9.5 -9.5" fill="none" stroke="#2c3f46" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CARDS = [
  { id: 'practice',   title: 'Practice',   tone: '#69b9af', Art: ArtPractice },
  { id: 'editorials', title: 'Editorials', tone: '#f5d9a6', Art: ArtEditorial },
  { id: 'backlog',    title: 'Backlog',    tone: '#e8806a', Art: ArtBacklog },
  { id: 'pdf',        title: 'PDFs',       tone: '#4b8376', Art: ArtPdf },
];

export default function Dashboard() {
  const [stats,      setStats]      = useState(null);   // null = loading, false = failed
  const [editorials, setEditorials] = useState(null);
  const [open,       setOpen]       = useState(null);   // which card's sheet is showing
  const [pdfBusy,    setPdfBusy]    = useState('');
  const [pdfError,   setPdfError]   = useState('');
  const sheetRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => {
    studyAPI.getStats()
      .then(res => setStats(res.data.stats))
      .catch(() => setStats(false));

    editorialsAPI.getEditorials()
      .then(res => setEditorials(res.data))
      .catch(() => setEditorials(false));
  }, []);

  const todayDate = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
  const downloadPrelims = async (date, answerSheet) => {
    const id = `${date}-${answerSheet ? 'answers' : 'paper'}`;
    setPdfBusy(id);
    setPdfError('');
    try { await downloadPrelimsPdf({ date, answerSheet }); }
    catch (error) { setPdfError(error.response?.data?.message || error.message || 'PDF download failed.'); }
    finally { setPdfBusy(''); }
  };

  /* Sheet: Esc closes, Tab stays inside, focus returns to the card */
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();

    const onKey = e => {
      if (e.key === 'Escape') { setOpen(null); return; }
      if (e.key !== 'Tab' || !sheetRef.current) return;
      const items = sheetRef.current.querySelectorAll('a[href], button:not([disabled])');
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      previous?.focus?.();
    };
  }, [open]);

  const sheetBody = () => {
    if (open === 'practice') return (
      <div className="dk-choices">
        <Link to="/prelims" className="dk-choice dk-choice--sky">Prelims</Link>
        <Link to="/mains" className="dk-choice dk-choice--rust">Mains</Link>
      </div>
    );

    if (open === 'editorials') {
      if (editorials === null) return <div className="dk-note"><span className="dk-spin" role="status" aria-label="Loading" /></div>;
      if (editorials === false) return <p className="dk-note">Could not load editorials.</p>;
      if (editorials.isSunday) return <p className="dk-note">No editorials on Sunday.</p>;
      const sources = [
        { label: 'The Hindu', color: 'var(--rust)', list: editorials.editorials?.hindu || [] },
        { label: 'Indian Express', color: 'var(--sky-d)', list: editorials.editorials?.ie || [] },
      ];
      return (
        <div className="dk-eds">
          {sources.map(src => (
            <section key={src.label}>
              <h3 className="dk-ed-head"><span className="dk-dot" style={{ background: src.color }} />{src.label}</h3>
              {src.list.length > 0 ? (
                <ul className="dk-ed-list">
                  {src.list.map((a, i) => (
                    <li key={i}>
                      <a href={a.url} target="_blank" rel="noopener noreferrer">
                        <span>{a.title}</span><ExternalLink size={14} />
                      </a>
                    </li>
                  ))}
                </ul>
              ) : <p className="dk-note dk-note--sm">None yet.</p>}
            </section>
          ))}
        </div>
      );
    }

    if (open === 'backlog') {
      if (stats === null) return <div className="dk-note"><span className="dk-spin" role="status" aria-label="Loading" /></div>;
      if (stats === false) return <p className="dk-note">Could not load backlog.</p>;
      if (!stats?.backlogBySubject?.length) return <p className="dk-note">All caught up.</p>;
      return (
        <ul className="dk-backlog">
          {stats.backlogBySubject.map(s => (
            <li key={s._id}>
              <span className="dk-subject">{s._id}</span>
              <span className="dk-pills">
                {s.prelims > 0 && <Link to={`/prelims/${encodeURIComponent(s._id)}`} className="dk-pill dk-pill--sky">Prelims <b>{s.prelims}</b></Link>}
                {s.mains > 0 && <Link to={`/mains/${encodeURIComponent(s._id)}`} className="dk-pill dk-pill--rust">Mains <b>{s.mains}</b></Link>}
              </span>
            </li>
          ))}
        </ul>
      );
    }

    if (open === 'pdf') return (
      <>
        <div className="dk-dls">
          {[
            { date: todayDate, answers: false, label: 'Today’s paper' },
            { date: todayDate, answers: true,  label: 'Today’s answers' },
            { date: 'all',     answers: false, label: 'All questions' },
            { date: 'all',     answers: true,  label: 'All answers' },
          ].map(item => {
            const id = `${item.date}-${item.answers ? 'answers' : 'paper'}`;
            return (
              <button key={id} type="button" className="dk-dl" onClick={() => downloadPrelims(item.date, item.answers)} disabled={!!pdfBusy}>
                <Download size={16} />{pdfBusy === id ? 'Preparing…' : item.label}
              </button>
            );
          })}
        </div>
        {pdfError && <p className="dk-error" role="alert">{pdfError}</p>}
      </>
    );

    return null;
  };

  const current = CARDS.find(c => c.id === open);

  return (
    <div className="dk">
      <h1 className="dk-sr">Dashboard</h1>

      <div className="dk-board">
        {/* One string across the four cards on wide screens */}
        <svg className="dk-string" viewBox="0 0 1000 80" preserveAspectRatio="none" aria-hidden="true">
          <path d="M0,18 Q500,62 1000,18" />
        </svg>
        <span className="dk-pin dk-pin--l" aria-hidden="true" />
        <span className="dk-pin dk-pin--r" aria-hidden="true" />

        <ul className="dk-slots">
          {CARDS.map((c, i) => (
            <li key={c.id} className="dk-slot">
              <div className="dk-hang">
                <span className="dk-wire" aria-hidden="true" />
                <button type="button" className="dk-card" aria-haspopup="dialog" onClick={() => setOpen(c.id)}>
                  <span className="dk-clip" aria-hidden="true" />
                  <span className="dk-flip">
                    <span className="dk-face dk-front">
                      <span className="dk-meta">{`0${i + 1}`}</span>
                      <span className="dk-panel" style={{ background: c.tone }}><c.Art /></span>
                      <span className="dk-title">{c.title}</span>
                      <span className="dk-row"><span className="dk-go"><ArrowUpRight size={17} /></span></span>
                    </span>
                    <span className="dk-face dk-back" aria-hidden="true">
                      <span className="dk-back-title">{c.title}</span>
                      <span className="dk-go dk-go--lg"><ArrowUpRight size={26} /></span>
                    </span>
                  </span>
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {current && (
        <div className="dk-overlay" onClick={e => { if (e.target === e.currentTarget) setOpen(null); }}>
          <div className="dk-sheet" role="dialog" aria-modal="true" aria-label={current.title} ref={sheetRef}>
            <div className="dk-sheet-head">
              <h2 className="dk-sheet-title">{current.title}</h2>
              <button type="button" className="dk-close" ref={closeRef} onClick={() => setOpen(null)} aria-label="Close"><X size={18} /></button>
            </div>
            <div className="dk-sheet-body">{sheetBody()}</div>
          </div>
        </div>
      )}
    </div>
  );
}